import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  AuthCredential,
  AuthStorage,
} from 'src/app/client/repositories/lib/auth';
import { NavItemType } from 'src/app/components/nav-items/models/nav-item-type.enum';
import { NavItemModel } from 'src/app/components/nav-items/models/nav-item.model';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {
  isSignedIn: boolean;
  private destroy$ = new Subject<void>();

  title: string = '';

  showCreateAccountButton = true;

  createAccountItem = {
    name: 'header.createAnAccount',
    type: NavItemType.Link,
    navigateTo: 'auth/signup',
    class: 'button-nav-link',
  } as NavItemModel;

  signInItem = {
    name: 'header.signIn',
    type: NavItemType.Profile,
    navigateTo: 'auth/signin',
    class: 'button-nav-link-profile',
  } as NavItemModel;

  contactItem = {
    name: 'header.contact',
    type: NavItemType.Link,
    navigateTo: '#',
    class: 'nav-header nav-contract',
  } as NavItemModel;

  homeItem = {
    name: 'header.home',
    type: NavItemType.Link,
    navigateTo: '/home',
    class: 'nav-header nav-home',
  } as NavItemModel;

  overviewItem = {
    name: 'header.overview',
    type: NavItemType.Link,
    navigateTo: '/overview',
    class: 'nav-header nav-overview',
  } as NavItemModel;

  claimsItem = {
    name: 'header.claims',
    type: NavItemType.Link,
    navigateTo: '/claims',
    class: 'nav-header nav-claims',
  } as NavItemModel;

  invoicesItem = {
    name: 'header.invoices',
    type: NavItemType.Link,
    navigateTo: '/invoices',
    class: 'nav-header nav-invoices',
  } as NavItemModel;

  navItems: Array<NavItemModel> = [
    this.contactItem,
    this.signInItem,
    this.createAccountItem,
  ];

  loggedInNavItems: Array<NavItemModel> = [
    this.homeItem,
    this.overviewItem,
    this.claimsItem,
    this.invoicesItem,
  ];

  navHighlightMap = {
    '/home': 'nav-home',
    '/overview': 'nav-overview',
    '/claims': 'nav-claims',
    '/invoices': 'nav-invoices',
  };

  constructor(
    private router: Router,
    private authStorage: AuthStorage
  ) {}

  signOut(): void {
    this.isSignedIn = false;
    this.authStorage.clear();
    localStorage.removeItem('confirmed');
    window.location.reload();
    this.router.navigate(['/']);
  }

  navigateToPasswordChange(): void {
    this.router.navigate(['/auth/set-new-password']);
  }

  ngOnInit(): void {
    this.updateNavItems();
    this.updateSignedInState();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateNavItems();
        this.updateSignedInState();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateNavItems(): void {
    if (this.router.url.includes('signin')) {
      this.navItems = [this.contactItem, this.createAccountItem];
    } else if (
      this.router.url.includes('signup') ||
      this.router.url.includes('confirm-signup') ||
      this.router.url.includes('confirmed-registration')
    ) {
      this.navItems = [this.contactItem, this.signInItem];
    } else {
      this.navItems = [
        this.contactItem,
        this.signInItem,
        this.createAccountItem,
      ];
    }
  }

  private updateSignedInState(): void {
    const credential = this.authStorage.getCredential();
    const hasValidCredential = !!credential && this.isValidCredential(credential);

    if (
      localStorage.getItem('confirmed') === 'true' &&
      hasValidCredential
    ) {
      this.isSignedIn = true;
      return;
    }

    this.isSignedIn = false;
    localStorage.removeItem('confirmed');

    if (hasValidCredential && this.isRegistrationRequestRoute()) {
      return;
    }

    localStorage.removeItem('fullName');

    if (!this.isPendingAuthChallenge(credential)) {
      this.authStorage.clear();
    }
  }

  tokenExpired(token?: string): boolean {
    if (!token) {
      return true;
    }

    try {
      const payload = token.split('.')[1];

      if (!payload) {
        return true;
      }

      const expiry = JSON.parse(atob(payload)).exp;
      return Math.floor(new Date().getTime() / 1000) >= expiry;
    } catch {
      return true;
    }
  }

  private isValidCredential(credential: AuthCredential): boolean {
    if (!environment.isSecure && credential.localAuth) {
      return true;
    }

    return !this.tokenExpired(credential.idToken);
  }

  private isPendingAuthChallenge(credential?: AuthCredential): boolean {
    if (!credential) {
      return false;
    }

    return !!credential.challengeName && !!credential.email && !credential.idToken;
  }

  private isRegistrationRequestRoute(): boolean {
    return this.router.url.includes('auth-registration-request');
  }
}
